import { useState } from 'react';
import { ShieldCheck, X, Loader, Lock } from 'lucide-react';
import { useAuth } from '../AuthContext';

const AdminUnlockModal = ({ open, onClose }) => {
  const { login } = useAuth();
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passcode || busy) return;
    setBusy(true);
    setError('');
    try {
      await login(passcode);
      // login() updates AuthContext.role globally; if admin, App rerenders.
      onClose();
      setPasscode('');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Sign in failed';
      setError(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-unlock-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-sm rounded-2xl shadow-xl border border-gray-200 p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="admin-unlock-close"
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 p-1"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="flex flex-col items-center mb-5 text-center">
          <div className="w-14 h-14 rounded-full bg-[#d97736]/15 flex items-center justify-center mb-3">
            <ShieldCheck className="text-[#d97736]" size={24} />
          </div>
          <h2
            className="text-xl text-[#0d1f15] tracking-tight"
            style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800 }}
          >
            Unlock Admin
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Enter the admin passcode to access Reports and inventory upload.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.25em] text-gray-500 mb-2">
              Admin Passcode
            </label>
            <input
              data-testid="admin-unlock-input"
              type="password"
              inputMode="numeric"
              autoFocus
              value={passcode}
              onChange={(e) => {
                setPasscode(e.target.value);
                if (error) setError('');
              }}
              placeholder="•••••••••"
              className="w-full h-12 px-4 text-lg tracking-widest text-center border border-gray-300 rounded-xl focus:outline-none focus:border-[#d97736] focus:ring-2 focus:ring-[#d97736]/20 transition"
            />
          </div>

          {error && (
            <div
              data-testid="admin-unlock-error"
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            >
              {error}
            </div>
          )}

          <button
            data-testid="admin-unlock-submit"
            type="submit"
            disabled={busy || !passcode}
            className="w-full bg-[#d97736] hover:bg-[#bd6224] text-white font-bold rounded-xl h-12 text-base transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {busy ? (
              <>
                <Loader className="animate-spin" size={18} /> Unlocking…
              </>
            ) : (
              <>
                <Lock size={16} /> Unlock
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminUnlockModal;
